import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { 
  Store, 
  Search, 
  Edit, 
  Trash2, 
  Plus,
  Download,
  RefreshCw,
  MapPin,
  Calendar,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

const ShopManagement = () => {
  const { 
    shops, 
    loading, 
    fetchShops, 
    updateShop, 
    deleteShop 
  } = useAdmin();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({});

  const loadShops = useCallback(async () => {
    const filters = {
      search: searchTerm,
      status: statusFilter !== 'all' ? statusFilter : undefined
    };
    
    const result = await fetchShops(currentPage, 20, filters);
    if (result?.pagination) {
      setPagination(result.pagination);
    }
  }, [currentPage, statusFilter, searchTerm, fetchShops]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleEditShop = (shop) => {
    setSelectedShop(shop);
    setShowEditModal(true);
  };

  const handleDeleteShop = (shop) => {
    setSelectedShop(shop);
    setShowDeleteModal(true);
  };

  const handleSaveShop = async (shopData) => {
    const result = await updateShop(selectedShop.id, shopData);
    if (result.success) {
      toast.success('Shop updated successfully');
      setShowEditModal(false);
      setSelectedShop(null);
      loadShops();
    } else {
      toast.error(result.error);
    }
  };

  const handleConfirmDelete = async () => {
    const result = await deleteShop(selectedShop.id);
    if (result.success) {
      toast.success('Shop deleted successfully');
      setShowDeleteModal(false);
      setSelectedShop(null);
      loadShops();
    } else {
      toast.error(result.error);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-600';
      case 'INACTIVE': return 'bg-red-600';
      case 'SUSPENDED': return 'bg-yellow-600';
      case 'PENDING': return 'bg-blue-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading && !shops.length) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">Loading shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{t('shopManagement.title')}</h1>
            <p className="text-gray-400">{t('shopManagement.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            <button className="btn-secondary">
              <Download className="w-4 h-4 mr-2" />
              {t('shopManagement.export')}
            </button>
            <button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              {t('shopManagement.addShop')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('shopManagement.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                className="input-field pl-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">{t('shopManagement.allStatus')}</option>
              <option value="ACTIVE">{t('shopManagement.status.ACTIVE')}</option>
              <option value="INACTIVE">{t('shopManagement.status.INACTIVE')}</option>
              <option value="SUSPENDED">{t('shopManagement.status.SUSPENDED')}</option>
              <option value="PENDING">{t('shopManagement.status.PENDING')}</option>
            </select>

            <button
              onClick={loadShops}
              className="btn-secondary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('shopManagement.refresh')}
            </button>
          </div>
        </div>

        {/* Shops Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(shops) && shops.map((shop) => (
            <div key={shop.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                    <Store className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{shop.name}</h3>
                    <p className="text-gray-400 text-sm">@{shop.slug}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusBadgeColor(shop.status)}`}>
                  {shop.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{shop.address || t('shopManagement.noAddress')}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{shop.owner?.firstName} {shop.owner?.lastName}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{t('shopManagement.joined', { date: new Date(shop.createdAt).toLocaleDateString() })}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditShop(shop)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  {t('shopManagement.edit')}
                </button>
                <button
                  onClick={() => handleDeleteShop(shop)}
                  className="flex-1 btn-danger text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('shopManagement.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-gray-400">
              {t('shopManagement.showing', { start: pagination.startItem, end: pagination.endItem, total: pagination.totalItems })}
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('shopManagement.previous')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('shopManagement.next')}
              </button>
            </div>
          </div>
        )}

        {/* Edit Shop Modal */}
        {showEditModal && selectedShop && (
          <EditShopModal
            shop={selectedShop}
            onSave={handleSaveShop}
            onClose={() => {
              setShowEditModal(false);
              setSelectedShop(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedShop && (
          <DeleteShopModal
            shop={selectedShop}
            onConfirm={handleConfirmDelete}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedShop(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Edit Shop Modal Component
const EditShopModal = ({ shop, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: shop.name,
    slug: shop.slug,
    description: shop.description || '',
    address: shop.address || '',
    phone: shop.phone || '',
    email: shop.email || '',
    status: shop.status
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">{t('shopManagement.editShop')}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.shopName')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.slug')}</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field w-full"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.address')}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.phone')}</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.email')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('shopManagement.statusLabel')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="input-field w-full"
            >
              <option value="ACTIVE">{t('shopManagement.status.ACTIVE')}</option>
              <option value="INACTIVE">{t('shopManagement.status.INACTIVE')}</option>
              <option value="SUSPENDED">{t('shopManagement.status.SUSPENDED')}</option>
              <option value="PENDING">{t('shopManagement.status.PENDING')}</option>
            </select>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              {t('shopManagement.saveChanges')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {t('shopManagement.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteShopModal = ({ shop, onConfirm, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">{t('shopManagement.deleteShop')}</h3>
        <p className="text-gray-400 mb-6">
          {t('shopManagement.deleteConfirm', { name: shop.name })}
        </p>
        <div className="flex space-x-3">
          <button onClick={onConfirm} className="btn-danger flex-1">
            {t('shopManagement.deleteConfirmAction')}
          </button>
          <button onClick={onClose} className="btn-secondary flex-1">
            {t('shopManagement.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopManagement;
